import React, { FC } from 'react';

import {
  Box,
  Chip,
  Link,
  MUITable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from 'components';

import { dayjs, useTranslation } from 'third-party';

import { DISPLAY_DATE_TIME_FORMAT } from 'constants/common';

import {
  BillingInvoice,
  CheckPurchase,
  CheckPurchaseStatus,
} from 'modules/billing';

import { formatUsd } from 'utils/helper';

const PERIOD_DATE_FORMAT = 'D MMM YYYY';

type Props = {
  purchases: CheckPurchase[];
  invoices: BillingInvoice[];
};

export const PaymentHistory: FC<Props> = ({ purchases, invoices }) => {
  const { t } = useTranslation();

  // Pack purchases live in our database and plan charges live in Stripe, but the customer only
  // wants one list of what they paid, newest first.
  const rows = [
    ...purchases.map(it => ({
      key: it.id,
      date: it.paidOn ?? it.createdOn,
      description: t('billing.history.packDescription', { count: it.quantity }),
      amount: it.totalAmount,
      isPaid: it.status === CheckPurchaseStatus.Paid,
      isFailed: it.status === CheckPurchaseStatus.Failed,
      receiptUrl: null as string | null,
    })),
    ...invoices.map(it => ({
      key: it.id,
      date: it.createdOn,
      description: t('billing.history.planDescription', {
        description: t('billing.history.planPeriod', {
          from: dayjs(it.createdOn).format(PERIOD_DATE_FORMAT),
          to: dayjs(it.createdOn).add(1, 'month').format(PERIOD_DATE_FORMAT),
        }),
      }),
      amount: it.amountPaid,
      isPaid: it.isPaid,
      isFailed: false,
      receiptUrl: it.hostedInvoiceUrl,
    })),
  ].sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf());

  const statusChip = (isPaid: boolean, isFailed: boolean) => {
    if (isPaid) {
      return (
        <Chip
          size="small"
          color="success"
          label={t('billing.purchase.status.paid')}
        />
      );
    }
    if (isFailed) {
      return (
        <Chip
          size="small"
          color="error"
          label={t('billing.purchase.status.failed')}
        />
      );
    }
    return <Chip size="small" label={t('billing.purchase.status.pending')} />;
  };

  return (
    <Box
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }} mb={1.5}>
        {t('billing.history.header')}
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('billing.history.empty')}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <MUITable size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('billing.purchase.column.date')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('billing.history.column.description')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('billing.purchase.column.total')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('billing.purchase.column.status')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('billing.history.column.receipt')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.key}>
                  <TableCell>
                    {dayjs(row.date).format(DISPLAY_DATE_TIME_FORMAT)}
                  </TableCell>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>
                    {t('billing.topUp.totalValue', {
                      total: formatUsd(row.amount),
                    })}
                  </TableCell>
                  <TableCell>{statusChip(row.isPaid, row.isFailed)}</TableCell>
                  <TableCell>
                    {row.receiptUrl ? (
                      <Link
                        href={row.receiptUrl}
                        target="_blank"
                        rel="noopener"
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {t('billing.history.view')}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MUITable>
        </Box>
      )}
    </Box>
  );
};
